import type { CharacterShape, Stroke } from "./stroke-shape.js";
import { strokeCost } from "./stroke-distance.js";

/**
 * Which characters a reader has drawn, ranked by how nearly they drew them.
 *
 * The comparison is stroke by stroke in writing order, and it is only ever a
 * comparison of two {@link CharacterShape}s, so the ink and the published index
 * arrive here having been through the same description. `stroke-shape.ts` is
 * that description.
 *
 * **A candidate needs at least as many strokes as the reader has drawn.**
 * Every extra one costs it a little, so a finished character beats the longer
 * characters that begin the same way, and a half-written one still brings back
 * a list. Three strokes of 海 come back as 氵 and four as 氾, 斗 and 汐, which
 * is a reader being shown that the pad has understood the 氵 they just wrote.
 *
 * **Stroke order and stroke count are relied on.** A reader who writes 忄 as
 * three strokes in the order the character is taught gets their character. One
 * who writes the two dots last, or draws 十 bottom to top, may not. Every
 * handwriting lookup of this kind makes that trade, and the alternative costs
 * an order of magnitude more work per candidate.
 */

/** What each stroke the reader has not yet drawn costs a candidate. */
const unwrittenStrokeCost = 0.25;

/** One candidate, and how nearly the reader drew it. */
export interface HandwritingCandidate {
  /** The character. */
  readonly character: string;

  /** How unlike the ink it is. Nought is a perfect match. */
  readonly cost: number;
}

/** One character of the index, ready to compare against. */
export interface HandwritingEntry {
  /** The character itself. */
  readonly character: string;

  /** Its strokes, in writing order. */
  readonly shape: CharacterShape;
}

/** Everything the index holds. */
export type HandwritingIndex = readonly HandwritingEntry[];

/** How unlike what the reader drew is to one character of the index. */
function characterCost(
  written: CharacterShape,
  stored: CharacterShape,
): number {
  let cost = 0;

  for (let at = 0; at < written.length; at += 1) {
    cost += strokeCost(written[at] as Stroke, stored[at] as Stroke);
  }

  return (
    cost / written.length +
    (stored.length - written.length) * unwrittenStrokeCost
  );
}

/** What a lookup needs. */
export interface HandwritingLookup {
  /** The index, as `handwriting-index.ts` read it. */
  readonly index: HandwritingIndex;

  /** What the reader has drawn so far. */
  readonly shape: CharacterShape;

  /** How many candidates to bring back. */
  readonly limit: number;
}

/**
 * The characters that most nearly match the ink, best first.
 *
 * A linear scan of the index, which is 9,276 characters and takes a few
 * milliseconds. There is no room here for an index over the index. The cost is
 * decided by the reader's stroke count, the pad calls this after every stroke,
 * and a scan that finishes inside a frame or two is already fast enough to
 * redraw the candidates before the reader has put their hand back down.
 */
export function lookUpHandwriting(
  lookup: HandwritingLookup,
): readonly HandwritingCandidate[] {
  const { index, shape, limit } = lookup;

  if (shape.length === 0 || limit <= 0) {
    return [];
  }

  const candidates: HandwritingCandidate[] = [];

  for (const entry of index) {
    if (entry.shape.length < shape.length) {
      continue;
    }

    candidates.push({
      character: entry.character,
      cost: characterCost(shape, entry.shape),
    });
  }

  candidates.sort((first, second) => first.cost - second.cost);

  return candidates.slice(0, limit);
}
